import type { TFunction } from "i18next";
import type { Theme } from "@mui/material/styles";
import { alpha, darken, lighten } from "@mui/material/styles";
import { DEFAULT_BRAND_COLOR } from "../../../themes/seedit-theme.tokens";
import {
  getHolidayEventColor,
  getHolidayLabelTextColor,
  SCHEDULE_HOLIDAY_BORDER_LIGHT
} from "../../../components/Schedule/ScheduleCalendar/functions/schedule-calendar-colors";
import type { DayWorkStatus, DayWorkStatusKind } from "../types/dashboard2-work-status.types";

const DASHBOARD2_OFFICE_GREEN_DARK = "#3DAB94";

/** Muted supporting accents for colleague chips — visible but subordinate to the user's row. */
const DASHBOARD2_COLLEAGUE_OFFICE_GREEN_LIGHT = "#6BA892";
const DASHBOARD2_COLLEAGUE_OFFICE_GREEN_DARK = "#7CB8A0";
const DASHBOARD2_COLLEAGUE_MOBILE_BLUE_LIGHT = "#8CB4E8";
const DASHBOARD2_COLLEAGUE_MOBILE_BLUE_DARK = "#9EC0EB";

/** Accent for office / workplace status — explicit green, distinct from info blue (mobile). */
export const officeStatusAccentColor = (theme: Theme): string =>
  theme.palette.mode === "dark" ? DASHBOARD2_OFFICE_GREEN_DARK : DEFAULT_BRAND_COLOR;

/** Brighter per-status accent for colleague booking chips in the week grid. */
export const colleagueStatusAccentColor = (
  theme: Theme,
  kind: Extract<DayWorkStatusKind, "office" | "mobile_working">
): string => {
  if (kind === "mobile_working") {
    return theme.palette.mode === "dark"
      ? DASHBOARD2_COLLEAGUE_MOBILE_BLUE_DARK
      : DASHBOARD2_COLLEAGUE_MOBILE_BLUE_LIGHT;
  }

  return theme.palette.mode === "dark"
    ? DASHBOARD2_COLLEAGUE_OFFICE_GREEN_DARK
    : DASHBOARD2_COLLEAGUE_OFFICE_GREEN_LIGHT;
};

export const holidayStatusSurfaceColors = (
  theme: Theme
): { bgcolor: string; border: string; color: string } => ({
  bgcolor: getHolidayEventColor(theme),
  border: SCHEDULE_HOLIDAY_BORDER_LIGHT,
  color: getHolidayLabelTextColor(theme)
});

/** Week planning day card — calendar holiday bar colors are for light cells only. */
export const weekDayHolidayCardSurface = (theme: Theme): { bgcolor: string; border: string } => {
  if (theme.palette.mode === "light") {
    const { bgcolor, border } = holidayStatusSurfaceColors(theme);

    return { bgcolor, border };
  }

  return {
    bgcolor: alpha(theme.palette.common.white, 0.06),
    border: alpha(theme.palette.common.white, 0.22)
  };
};

export type StatusSurfaceInk = {
  primary: string;
  secondary: string;
};

/** Readable ink on tinted status surfaces — hue-aligned, not grey-on-color (WCAG AA). */
export const statusSurfaceInkColors = (theme: Theme, kind: DayWorkStatusKind): StatusSurfaceInk => {
  const isDark = theme.palette.mode === "dark";

  if (kind === "unplanned" || kind === "other_bookings") {
    return {
      primary: isDark ? theme.palette.common.white : theme.palette.text.primary,
      secondary: isDark ? alpha(theme.palette.common.white, 0.78) : theme.palette.text.secondary
    };
  }

  if (kind === "public_holiday") {
    const { color } = holidayStatusSurfaceColors(theme);

    return {
      primary: color,
      secondary: isDark ? lighten(color, 0.12) : darken(color, 0.12)
    };
  }

  const accent = statusAccentColor(theme, kind);

  if (isDark && (kind === "office" || kind === "mobile_working")) {
    return {
      primary: theme.palette.text.primary,
      secondary: theme.palette.text.secondary
    };
  }

  if (isDark) {
    return {
      primary: lighten(accent, 0.72),
      secondary: lighten(accent, 0.55)
    };
  }

  return {
    primary: darken(accent, 0.58),
    secondary: darken(accent, 0.42)
  };
};

/** Week day card ink — holiday calendar bar colors are unreadable on neutral dark cards. */
export const weekDayStatusInkColors = (theme: Theme, kind: DayWorkStatusKind): StatusSurfaceInk => {
  if (kind === "public_holiday" && theme.palette.mode === "dark") {
    return {
      primary: theme.palette.text.primary,
      secondary: theme.palette.text.secondary
    };
  }

  return statusSurfaceInkColors(theme, kind);
};

/** Week day card icon rail — holiday calendar border is too faint on dark surfaces. */
export const weekDayStatusAccentColor = (theme: Theme, kind: DayWorkStatusKind): string => {
  if (kind === "public_holiday" && theme.palette.mode === "dark") {
    return alpha(theme.palette.text.primary, 0.72);
  }

  return statusAccentColor(theme, kind);
};

export const statusAccentColor = (theme: Theme, kind: DayWorkStatusKind): string => {
  if (kind === "office") {
    return officeStatusAccentColor(theme);
  }
  if (kind === "mobile_working") {
    return theme.palette.info.main;
  }
  if (kind === "public_holiday") {
    return holidayStatusSurfaceColors(theme).border;
  }
  if (kind === "other_bookings") {
    return theme.palette.secondary.main;
  }
  return theme.palette.text.secondary;
};

export const statusHeadline = (status: DayWorkStatus, t: TFunction): string => {
  if (status.kind === "unplanned") {
    return t("_dashboard2_statusNotSet");
  }
  if (status.kind === "mobile_working") {
    return t("_dashboard2_statusMobileWorking");
  }
  if (status.kind === "public_holiday") {
    return status.holidayName ?? status.label;
  }
  return status.label;
};

export const statusKindToAction = (
  kind: DayWorkStatusKind
): "office" | "mobile_working" | "unplanned" | null => {
  if (kind === "office") {
    return "office";
  }
  if (kind === "mobile_working") {
    return "mobile_working";
  }
  if (kind === "unplanned") {
    return "unplanned";
  }
  return null;
};

/** Compact colleague row on the week planning board. */
export const weekColleagueStatusLabel = (status: DayWorkStatus, t: TFunction): string => {
  const action = statusKindToAction(status.kind);

  if (action) {
    return statusActionLabel(status, t);
  }

  return statusHeadline(status, t);
};

export const statusActionLabel = (status: DayWorkStatus, t: TFunction): string => {
  const action = statusKindToAction(status.kind);

  if (action === "office") {
    return t("_dashboard2_statusActionOffice");
  }
  if (action === "mobile_working") {
    return t("_dashboard2_statusActionMobile");
  }
  if (action === "unplanned") {
    return t("_dashboard2_statusActionNotSet");
  }

  return statusHeadline(status, t);
};

const joinStatusParts = (parts: Array<string | undefined>): string =>
  parts.filter((part): part is string => Boolean(part?.trim())).join(" · ");

/**
 * Colleague strip — status word on the identity row (Office, Mobile working, …).
 */
export const colleagueStatusActionLine = (status: DayWorkStatus, t: TFunction): string =>
  weekColleagueStatusLabel(status, t);

/** Colleague strip — location · floor · desk for office bookings; omitted otherwise. */
export const colleagueStatusPlaceLine = (status: DayWorkStatus): string | undefined => {
  if (status.kind !== "office") {
    return undefined;
  }

  const placeLine = joinStatusParts([status.label, status.floorName, status.detail]);

  return placeLine || undefined;
};

/**
 * Colleague strip secondary line: status word plus location context for office
 * bookings (location · floor · desk), or the plain status word otherwise.
 */
export const colleagueStatusDetailLine = (status: DayWorkStatus, t: TFunction): string => {
  const statusWord = colleagueStatusActionLine(status, t);
  const placeLine = colleagueStatusPlaceLine(status);

  if (placeLine) {
    return joinStatusParts([statusWord, placeLine]) || statusWord;
  }

  return statusWord;
};

export const extraBookingCount = (status: DayWorkStatus): number => {
  const listedCount = status.additionalBookings?.length ?? 0;

  if (listedCount > 0) {
    return listedCount;
  }

  return status.secondaryBookingCount ?? 0;
};

/** Week planning board — headline only (Office / Mobile / Not set / holiday). */
export const weekPlanningPillPrimaryLine = (status: DayWorkStatus, t: TFunction): string =>
  weekColleagueStatusLabel(status, t);

/** Week planning board — at most one detail line (desk for office; plan hint when unplanned). */
export const weekPlanningPillSecondaryLine = (
  status: DayWorkStatus,
  t?: TFunction
): string | undefined => {
  if (status.kind === "office") {
    return status.detail?.trim() || undefined;
  }

  if (status.kind === "unplanned" && t) {
    return t("_dashboard2_planThisDay");
  }

  return undefined;
};

/** Top line for today / rich status pills: location and floor for office bookings. */
export const statusPillPrimaryLine = (status: DayWorkStatus, t: TFunction): string => {
  if (status.kind === "office") {
    const placeLine = joinStatusParts([status.label, status.floorName]);

    if (placeLine) {
      return placeLine;
    }
  }

  return statusHeadline(status, t);
};

/**
 * Label on the today work-status collapse trigger.
 */
export const todayWorkStatusTriggerLabel = (status: DayWorkStatus, t: TFunction): string => {
  if (status.kind === "unplanned") {
    return t("_dashboard2_tapToPlanToday");
  }

  return statusActionLabel(status, t);
};

/** Bottom line for week-planning status pills: inventory, time, and optional +N more. */
export const statusPillSecondaryLine = (
  status: DayWorkStatus,
  t?: TFunction
): string | undefined => {
  let baseLine: string | undefined;

  if (status.kind === "office") {
    baseLine = joinStatusParts([status.detail, status.timeRange]) || undefined;
  } else {
    baseLine = status.detail;
  }

  const extraCount = extraBookingCount(status);

  if (extraCount > 0 && t) {
    const extraLabel = t("_dashboard2_statusMoreBookingsShort", { count: extraCount });

    if (baseLine) {
      return `${baseLine} · ${extraLabel}`;
    }

    return extraLabel;
  }

  return baseLine;
};

export const statusPillOfficeInventoryLine = (status: DayWorkStatus): string | undefined => {
  if (status.kind !== "office") {
    return undefined;
  }

  return status.detail?.trim() || undefined;
};

export const statusFinishedTimeLine = (status: DayWorkStatus, t: TFunction): string | undefined => {
  const timeRange = status.timeRange?.trim();

  if (!status.isFinished) {
    return timeRange || undefined;
  }

  const finishedLabel = t("_dashboard2_statusFinished");

  if (timeRange) {
    return `${timeRange} · ${finishedLabel}`;
  }

  return finishedLabel;
};

export const statusPillOfficeTimeLine = (
  status: DayWorkStatus,
  t?: TFunction
): string | undefined => {
  if (status.kind !== "office") {
    return undefined;
  }

  if (status.isFinished && t) {
    return statusFinishedTimeLine(status, t);
  }

  return status.timeRange?.trim() || undefined;
};

export const statusPillExtraBookingsShortLine = (
  status: DayWorkStatus,
  t: TFunction
): string | undefined => {
  const extraCount = extraBookingCount(status);

  if (extraCount > 0) {
    return t("_dashboard2_statusMoreBookingsShort", { count: extraCount });
  }

  return undefined;
};
